import { FC, useEffect, useState } from 'react'
import { Button, Col, message, Modal, Row, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { useGate } from 'statsig-react'
import { Plus } from '@phosphor-icons/react'
import CardStop, { CardStopProps, ItemStop } from './components/card-stop'
import FormStop from './components/form-stop'
import { containerBody } from './styles'
import { odinJobRepository } from '../odin-job.repository'
import { Job } from '../../domain/job'
import { deleteStopUseCase } from '../../application/delete-stop.use-case copy'
import { odinNebulaMultistopRepository } from '../odin-nebula-multistop.repository'
import { DURATION_MESSAGES_IN_SEC, STOP_COLORS } from './constants'
import { getJobUseCase } from '../../application/get-job.use-case'
import { StepState, TaskNames } from '../../domain/task'

const { Text } = Typography

interface MultistopModalProps {
  job: Job
  diabledCreateStop: boolean
  handleCancel: () => void
  setSelectedJob: (job?: Job) => void
}

const MultistopModal: FC<MultistopModalProps> = ({ job, diabledCreateStop, handleCancel, setSelectedJob }) => {
  const { t } = useTranslation()

  const [stops, setStops] = useState<CardStopProps[]>([])
  const [showFormStop, setShowFormStop] = useState<boolean>(false)
  const [editStop, setEditStop] = useState<CardStopProps | undefined>()
  const isGetDetailJobByNebulaActive = useGate('is_get_detail_job_by_nebula_active').value

  useEffect(() => {
    getJob()
  }, [])

  useEffect(() => {
    if (job?.items?.length) {
      buildStops()
    }
  }, [job])

  const getJob = async () => {
    try {
      if (isGetDetailJobByNebulaActive) {
        setSelectedJob()
      } else {
        const updatedJob = await getJobUseCase(odinJobRepository)(job.uuid)
        setSelectedJob({ ...job, items: updatedJob.items, tasks: updatedJob.tasks })
      }
    } catch (_) {}
  }

  const deleteStop = async (storeId: string) => {
    try {
      await deleteStopUseCase(odinNebulaMultistopRepository)(job.uuid, { store_id: storeId })
      message.success(t('multistop.success-delete-stop'), DURATION_MESSAGES_IN_SEC)
    } catch (_) {
      message.error(t('multistop.error-delete-stop'), DURATION_MESSAGES_IN_SEC)
    }
  }

  const handleDeleteStop = (storeId: string) => {
    const modal = Modal.confirm({
      title: t('multistop.confirm-delete-stop.title'),
      content: t('multistop.confirm-delete-stop.message'),
      okText: t('multistop.confirm-delete-stop.confirm'),
      cancelText: t('multistop.confirm-delete-stop.cancel'),
      zIndex: 10001002,
      onOk: async () => {
        modal.update({ okButtonProps: { loading: true } })
        await deleteStop(storeId)
        await getJob()
        modal.update({ okButtonProps: { loading: false } })
      }
    })
  }

  const handleEditStop = (storeId: string) => {
    const findStop = stops.find((stop) => stop.id === storeId)
    if (findStop) {
      setEditStop((_) => ({ ...findStop }))
      setShowFormStop(true)
    }
  }

  const getStopColors = (order: number): { bgColorTag: string; textColorTag: string } => {
    const colorIndex = order % STOP_COLORS.length
    const { bg, text } = STOP_COLORS[colorIndex]
    return {
      bgColorTag: bg,
      textColorTag: text
    }
  }

  const buildStops = () => {
    const itemsByTask = job.items.reduce((acc, item) => {
      if (!acc[item.taskId]) {
        acc[item.taskId] = []
      }
      acc[item.taskId].push({ sku: item.attributes.sku, id: item.itemId })
      return acc
    }, {} as Record<string, ItemStop[]>)

    const stops: CardStopProps[] = job.tasks.reduce((acc, task, index) => {
      const taskItems = itemsByTask[task.uuid] || []

      if (taskItems.length > 0) {
        const isOrigin = task.storeId === job.storeId
        const title = isOrigin ? t('origin') : `${t('stop')} ${task.stopMetadata?.order ?? index + 1}`

        const stopDetails: CardStopProps = {
          id: task.storeId ?? job.storeId,
          title,
          storeName: task.stopMetadata?.storeName ?? job.origin.name,
          items: taskItems,
          isOrigin,
          status: task?.steps?.find((step) => step.type.name === TaskNames.PICKING).status || StepState.DONE
        }

        if (!isOrigin) {
          const { bgColorTag, textColorTag } = getStopColors(index)
          stopDetails.bgColorTag = bgColorTag
          stopDetails.textColorTag = textColorTag
        }

        acc.push(stopDetails)
      }

      return acc
    }, [] as CardStopProps[])

    setStops((_) => sortCardStops(stops))
  }

  const hanleSaveFormStop = () => {
    setShowFormStop(false)
    setEditStop((_) => undefined)
    getJob()
  }

  const handleCancelFormStop = () => {
    setShowFormStop(false)
    setEditStop((_) => undefined)
  }

  const sortCardStops = (stops: CardStopProps[]): CardStopProps[] => {
    return stops.sort((a, b) => {
      if (a.isOrigin !== b.isOrigin) {
        return Number(b.isOrigin) - Number(a.isOrigin)
      }

      if (!a.isOrigin && !b.isOrigin) {
        return a.title.localeCompare(b.title)
      }

      return 0
    })
  }

  return (
    <Modal
      title={t('multistop.modal.title')}
      visible={true}
      mask={true}
      onCancel={handleCancel}
      footer={null}
      width={'40%'}
      bodyStyle={{ height: 600, minHeight: 600 }}
      zIndex={10001001}
    >
      <Text style={{ fontWeight: 400 }}>
        {t('multistop.modal.description_first')} <strong>{t('multistop.modal.description_second')}</strong>
      </Text>

      {!showFormStop && (
        <>
          <Row
            gutter={[0, 16]}
            style={{ ...containerBody, overflowY: 'auto', overflowX: 'hidden', maxHeight: `430px` }}
          >
            {stops.map((stop, index) => (
              <Col key={`${index}`} style={{ width: '100%' }}>
                <CardStop
                  key={stop.id}
                  {...stop}
                  {...(!stop.isOrigin && { handleEdit: handleEditStop, handleDelete: handleDeleteStop })}
                />
              </Col>
            ))}
          </Row>
          <Button
            type='link'
            icon={<Plus />}
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            onClick={() => setShowFormStop(true)}
            disabled={diabledCreateStop}
          >
            {t('multistop.add-stop')}
          </Button>
        </>
      )}

      {showFormStop && (
        <>
          <FormStop
            jobUuid={job.uuid}
            stops={stops}
            items={job.items}
            currentStop={editStop}
            hanleCancel={handleCancelFormStop}
            hanleSave={hanleSaveFormStop}
          />
        </>
      )}
    </Modal>
  )
}

export default MultistopModal
